import { Icon } from "@blueprintjs/core"
import { format, parseISO } from "date-fns"
import React from "react"
import { graphql, useFragment } from "react-relay"

import styles from "./IntegrationPanel.module.css"

import { IntegrationSyncStatus_integration$key } from "./__generated__/IntegrationSyncStatus_integration.graphql"

import { Timer } from "~/common/Timer"
import { PauseIcon, WarningIcon } from "~/common/react-icons"

import { IntegrationSyncIcon } from "./IntegrationSyncIcon"

type ContainerProps = {
  children: React.ReactNode
}

const Container = (props: ContainerProps) => {
  const { children } = props
  return <div className={styles.statusContainer}>{children}</div>
}

const INTEGRATION_SYNC_STATUS_FRAGMENT = graphql`
  fragment IntegrationSyncStatus_integration on integration {
    state
    active
    last_sync_at
    integration_syncs(order_by: { id: desc }, limit: 1) {
      id
      state
      started_at
      finished_at
      info_messages
    }
  }
`
type IntegrationSyncStatusProps = {
  integration: IntegrationSyncStatus_integration$key
  onOpenSyncHistory: () => void
}

const IntegrationSyncStatus = (props: IntegrationSyncStatusProps) => {
  const { onOpenSyncHistory } = props

  const integration = useFragment(
    INTEGRATION_SYNC_STATUS_FRAGMENT,
    props.integration,
  )

  const lastIntegrationSync = integration.integration_syncs[0]
  const lastSyncAt = integration.last_sync_at
    ? parseISO(integration.last_sync_at)
    : undefined

  if (!integration.active) {
    return (
      <Container>
        <PauseIcon />
        <p className={styles.bold}>
          Integration is currently turned off.
          {lastSyncAt && ` Last synced at ${format(lastSyncAt, "PP p")}`}
        </p>
      </Container>
    )
  }

  if (integration.state === "ERROR") {
    return (
      <Container>
        <WarningIcon />
        <p className={styles.bold}>Sync error</p>
      </Container>
    )
  }

  if (lastIntegrationSync && !lastIntegrationSync.finished_at) {
    return (
      <Container>
        <IntegrationSyncIcon animated onClick={onOpenSyncHistory} />
        <p className={styles.bold}>
          Syncing … <Timer startAt={lastSyncAt} liveUpdate />
        </p>
      </Container>
    )
  }

  if (lastIntegrationSync && lastIntegrationSync.state === "FAILED") {
    return (
      <Container>
        <WarningIcon onClick={onOpenSyncHistory} />
        <p className={styles.bold}>Last sync failed!</p>
      </Container>
    )
  }

  if (integration.state === "READY") {
    return (
      <Container>
        <Icon icon="tick-circle" onClick={onOpenSyncHistory} />
        <p className={styles.bold}>
          Last synced at {lastSyncAt ? format(lastSyncAt, "PP p") : "--"}
        </p>
      </Container>
    )
  }

  return null
}

export { IntegrationSyncStatus }
